// Global timer registry for shared debounce keys
const timerRegistry = new Map<string, NodeJS.Timeout | null>()

/**
 * Debounce a function to prevent it from being called too frequently.
 *
 * @param fn The function to debounce.
 * @param delay The time in milliseconds to wait before calling the debounced function. Default is 500ms.
 * @param key Optional string key to share timers across multiple debounced functions. Functions with the same key will cancel each other's timers.
 * @returns An object containing the debounced function and a cancel function.
 *
 * @example
 * // Basic usage
 * const { debouncedFn, cancel } = debounce(() => alert('Hello!'), 1000)
 *
 * // Shared timer usage
 * const { debouncedFn: saveText, cancel: cancelText } = debounce(() => saveTextData(), 100, 'save')
 * const { debouncedFn: saveNonText, cancel: cancelNonText } = debounce(() => saveNonTextData(), 500, 'save')
 * // Both saveText and saveNonText will cancel each other's timers because they share the 'save' key
 *
 * // Call the debounced function repeatedly within a short period
 * debouncedFn()
 * debouncedFn()
 * debouncedFn()
 * // After a delay of 1 second (1000ms), the debounced function will be called only once
 *
 * // Cancel any pending calls
 * cancel()
 */
export const debounce = <T>(
    fn: () => T,
    delay: number,
    key?: string
): { debouncedFn: () => void; cancel: () => void } => {
    let timer: NodeJS.Timeout | null

    const debouncedFn = (): void => {
        // If using a shared key, cancel any existing timer for that key
        if (key) {
            const existingTimer = timerRegistry.get(key)
            if (existingTimer) {
                clearTimeout(existingTimer)
            }
        } else if (timer) {
            // For non-shared timers, cancel the local timer
            clearTimeout(timer)
        }

        // Create new timer
        const newTimer = setTimeout(fn, delay)

        // Store timer reference
        if (key) {
            timerRegistry.set(key, newTimer)
        } else {
            timer = newTimer
        }
    }

    const cancel = (): void => {
        if (key) {
            // Cancel shared timer
            const existingTimer = timerRegistry.get(key)
            if (existingTimer) {
                clearTimeout(existingTimer)
                timerRegistry.set(key, null)
            }
        } else if (timer) {
            // Cancel local timer
            clearTimeout(timer)
            timer = null
        }
    }

    return { debouncedFn, cancel }
}
